// ZIP reader, counterpart to the writer in ./zip.ts. Method 8 inflates with fflate.
// unzipSync is not the whole reader: it rejects the archive on one bad member and does not
// surface the encryption flag, and one bad entry must not drop its siblings.
// Parsing starts from the END: the EOCD record locates the central directory, whose
// sizes/offsets are authoritative — local headers may hold zeros when general-purpose bit 3
// (data descriptor) is set, so they are read only to find where each entry's data starts.
// Anything malformed or unsupported skips that one entry with a single console.warn.

import { inflateSync, strFromU8 } from "fflate";

export interface ZipEntry {
  name: string;
  bytes: Uint8Array;
}

const LOCAL_SIG = 0x04034b50;
const CENTRAL_SIG = 0x02014b50;
const EOCD_SIG = 0x06054b50;

function inflateRaw(data: Uint8Array): Uint8Array {
  return inflateSync(data);
}

// EOCD = 22 fixed bytes + up to 65535 bytes of trailing archive comment, so the signature can sit
// anywhere in the last 65557 bytes; scan backwards and take the LAST occurrence (nearest the end).
function findEocd(bytes: Uint8Array, dv: DataView): number {
  const min = Math.max(0, bytes.length - 22 - 0xffff);
  for (let i = bytes.length - 22; i >= min; i--) {
    if (dv.getUint32(i, true) === EOCD_SIG) return i;
  }
  return -1;
}

// Parse a ZIP archive into its file entries. Never throws on bad input: an unreadable archive returns
// and an unreadable ENTRY is dropped, each with one console.warn.
export async function unzip(bytes: Uint8Array): Promise<ZipEntry[]> {
  const out: ZipEntry[] = [];
  if (bytes.length < 22) {
    console.warn("unzip: buffer smaller than an empty ZIP — ignoring");
    return out;
  }
  const dv = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const eocd = findEocd(bytes, dv);
  if (eocd < 0) {
    console.warn(
      "unzip: no end-of-central-directory record — not a ZIP (or truncated); ignoring",
    );
    return out;
  }
  const count = dv.getUint16(eocd + 10, true); // total central-directory entries (0xFFFF would mean zip64)
  let off = dv.getUint32(eocd + 16, true); // central directory start
  for (let i = 0; i < count; i++) {
    // The central directory must end before the EOCD; a short/unsigned record means the count or the
    // offsets lied — stop walking rather than misread garbage as entries.
    if (off + 46 > eocd || dv.getUint32(off, true) !== CENTRAL_SIG) {
      console.warn(
        "unzip: central directory truncated or corrupt — stopping early",
      );
      break;
    }
    const flags = dv.getUint16(off + 8, true);
    const method = dv.getUint16(off + 10, true);
    const csize = dv.getUint32(off + 20, true);
    const usize = dv.getUint32(off + 24, true);
    const nlen = dv.getUint16(off + 28, true);
    const xlen = dv.getUint16(off + 30, true);
    const clen = dv.getUint16(off + 32, true);
    const lho = dv.getUint32(off + 42, true); // local header offset
    const nameBytes = bytes.subarray(off + 46, off + 46 + nlen);
    // Bit 11 = UTF-8. Without it, names are formally cp437; real files are latin1.
    const name = strFromU8(nameBytes, (flags & 0x0800) === 0);
    off += 46 + nlen + xlen + clen;
    try {
      if (name.endsWith("/")) continue; // directory entry — nothing to extract, and not worth a warn
      if (flags & 0x0001) {
        console.warn(`unzip: "${name}" is encrypted — skipping`);
        continue;
      }
      // 0xFFFFFFFF is the zip64 sentinel (real value lives in an extra field we don't parse).
      if (csize === 0xffffffff || usize === 0xffffffff || lho === 0xffffffff) {
        console.warn(`unzip: "${name}" needs zip64 — skipping`);
        continue;
      }
      if (method !== 0 && method !== 8) {
        console.warn(
          `unzip: "${name}" uses unsupported compression method ${method} — skipping`,
        );
        continue;
      }
      if (lho + 30 > bytes.length || dv.getUint32(lho, true) !== LOCAL_SIG) {
        console.warn(
          `unzip: "${name}" has a bad local-header offset — skipping`,
        );
        continue;
      }
      // Data starts after the LOCAL header's name+extra (their lengths can differ from the central
      // copy — e.g. tools that put timestamps only in one of the two extra fields).
      const dataStart =
        lho + 30 + dv.getUint16(lho + 26, true) + dv.getUint16(lho + 28, true);
      if (dataStart + csize > bytes.length) {
        console.warn(
          `unzip: "${name}" data runs past the end of the buffer — skipping`,
        );
        continue;
      }
      const comp = bytes.subarray(dataStart, dataStart + csize);
      // Stored: copy (not subarray) so returned entries don't pin the whole archive buffer alive.
      const data = method === 0 ? comp.slice() : inflateRaw(comp);
      if (data.length !== usize) {
        console.warn(
          `unzip: "${name}" inflated to ${data.length} B, expected ${usize} — skipping`,
        );
        continue;
      }
      out.push({ name, bytes: data });
    } catch {
      console.warn(`unzip: "${name}" failed to decompress — skipping`);
    }
  }
  return out;
}
